import React, { useState, useRef, useCallback } from "react";
import { AgGridReact } from "ag-grid-react";
import { GridOptions, GridApi, IRowNode } from "ag-grid-enterprise";
import * as GridUtils from "../../utils/gridUtils";
import { columns } from "./procGridColDef";
import MergedChildModal from "../ui/MergedChildModal";
import { MergedChild } from "../../models/Po";

// ui components
import ProcListLayoutSelecter from "../ui/ProcListLayoutSelecter";
import ToggleSwitch from "../ui/ToggleSwitch";
import DateTimeEditor from "../ui/DateTimeEditor";
import RunningTotal from "../RunningTotal";
// chart components
import Chart from "../charts/Chart";
import CostChart from "../charts/CostChart";
// redux
import { useDispatch, useSelector } from "react-redux";
import { toggleTemperingScheduleMode } from "../../redux/temperingScheduleSlice";
// import { toggleTemperingScheduleMode } from '../../redux/temperingScheduleSlice';
import { AppDispatch, RootState } from "../../redux/store";
import { setCheckedRowUpdateAt } from "../../redux/procGridSlice";
// CSS
import "./ProcGrid.css";
import "ag-grid-enterprise/styles/ag-grid.css";
import "ag-grid-enterprise/styles/ag-theme-alpine.css";
import {
  DefaultLayouts,
  CHECK_BOX_DEF,
  SORT_DEF,
} from "../../utils/defaultLayout";
import cloneDeep from "lodash/cloneDeep";
interface GridProps {
  rowData: any[];
  onGridReady: (params: { api: any; columnApi: any | null }) => void;
}

// Gridをフィル操作（セルの右下をドラッグ）する時に呼ばれる関数について
interface FillOperationParams {
  api: GridApi;
  rowNode: {
    rowIndex: number | null | undefined;
  };
  values: any[];
}

// 前の行の時刻に加工時間を加算して新しい時刻を計算する関数
const calculateFillOperation = (params: FillOperationParams) => {
  const api: GridApi = params.api;
  const rowIndex = params.rowNode.rowIndex;
  const defaultValue = params.values[0]; // valuesプロパティからデフォルト値を取得

  if (rowIndex !== null && rowIndex !== undefined) {
    // rowIndexがnullまたはundefinedでないことを確認
    const prevRowNode = api.getDisplayedRowAtIndex(rowIndex - 1);
    if (prevRowNode) {
      const prevScheduledDate = prevRowNode.data.edited_schedule_time_start;
      const prevSimulatedProcTime = prevRowNode.data.simulated_proc_time; // 前行のsimulated_proc_timeを取得
      // ここで prevScheduledDate と prevSimulatedProcTime を使って新しい日時を計算
      const newScheduledDate = GridUtils.calculateNewDate(
        prevScheduledDate,
        prevSimulatedProcTime
      );
      return newScheduledDate;
    } else {
      // 1行目の場合、適切な処理を行う（例：デフォルト値を使用する、エラーメッセージを表示するなど）
      console.warn("No previous row found. Using default value.");
      return defaultValue;
    }
  }
  return defaultValue; // デフォルト値を返す
};

const handleRowDragEnd = (event: any, api: GridApi | null | undefined) => {
  console.log("Drag end event: ", event);

  if (api != null) {
    const selectedNodes: IRowNode<any>[] = [];
    api.forEachNode((node) => {
      if (node.isSelected()) {
        selectedNodes.push(node);
      }
    });

    if (selectedNodes.length > 0) {
      const allRows: any[] = [];
      api.forEachNode((node) => {
        allRows.push(node.data);
      });

      const selectedRows = selectedNodes.map((node) => node.data);
      const remainingRows = allRows.filter(
        (row) => !selectedRows.includes(row)
      );

      remainingRows.splice(event.overIndex, 0, ...selectedRows);

      api.setRowData(remainingRows);
    }
  }
};

// rowDataの管理はreduxを使うと、編集処理が遅くなるのでreduxを使わないことにした。
const Grid: React.FC<GridProps> = ({ rowData, onGridReady }) => {
  const gridRef = useRef<any>();
  const dispatch: AppDispatch = useDispatch();
  const isSchedulingMode = useSelector(
    (state: RootState) => state.temperingSchedule.isSchedulingMode
  );
  // 選択したレイアウト
  const selectLayout = useSelector(
    (state: RootState) => state.procListLayout.selectLayout
  );
  const [gridApi, setGridApi] = useState<GridApi | null>(null); // Ag-Grid APIを保存するステート
  const [columnApi, setColumnApi] = useState<any | null>(null); // columnApiの型はAg-Gridのバージョンによって異なる可能性があります。
  const [filteredRowData, setFilteredRowData] = React.useState(rowData); // 新たな状態変数
  const [gridHeight, setGridHeight] = useState("calc(100vh - 360px"); // gridの高さ
  const [isShowGraph, setIsShowGraph] = useState(false); // グラフ表示

  // 追加始まり 2024/06/02
  const [isMergedChildModalOpen, setIsMergedChildModalOpen] = useState(false);
  const [selectedMergedChildren, setSelectedMergedChildren] = useState<
    MergedChild[]
  >([]);

  const openMergedChildModal = useCallback((mergedChildren: MergedChild[]) => {
    setSelectedMergedChildren(mergedChildren);
    setIsMergedChildModalOpen(true);
  }, []);

  const closeMergedChildModal = () => {
    setIsMergedChildModalOpen(false);
  };
  // 追加終わり 2024/06/02

  const onGridReadyHandler = (params: { api: GridApi; columnApi: any }) => {
    setGridApi(params.api); // APIを保存
    setColumnApi(params.columnApi); // columnApiも保存
    onGridReady(params);
  };

  const handleChangeGraphTggle = () => {
    setIsShowGraph(!isShowGraph);
  };

  // グラフ表示非表示にあわせてgrid領域の高さを調整
  React.useEffect(() => {
    if (isShowGraph) {
      setGridHeight("calc(100vh - 580px");
    } else {
      setGridHeight("calc(100vh - 360px");
    }
  }, [isShowGraph]);

  // rowDataが変更された場合にfilteredRowDataも更新
  React.useEffect(() => {
    setFilteredRowData(rowData);
  }, [rowData]);

  // レイアウト変更
  // comparatorとか関数をオブジェクトの扱いを検討
  // APIだと使用する関数名うけとって、クライアントで置き換えるなど
  React.useEffect(() => {
    console.log("selectLayout: ", selectLayout);
    if (selectLayout == null) {
      // コード上にもサーバにもレイアウト定義が存在しない場合
      // コード上に定義したlayout_id=0の定義を使用する
      const defLayout = DefaultLayouts.filter((d) => d.layout_id === 0);
      gridApi?.setColumnDefs([
        CHECK_BOX_DEF,
        SORT_DEF,
        ...defLayout[0].columns,
      ]);
    } else {
      //　コード上にレイアウト定義が存在する場合
      const defLayout = DefaultLayouts.filter(
        (d) => d.layout_id === selectLayout.layout_id
      );
      if (defLayout.length > 0) {
        gridApi?.setColumnDefs([
          CHECK_BOX_DEF,
          SORT_DEF,
          ...defLayout[0].columns,
        ]);
      } else {
        // サーバにレイアウト定義が存在する場合
        gridApi?.setColumnDefs(
          GridUtils.convertColumnsDef(cloneDeep(selectLayout.columns))
        );
      }
    }
  }, [gridApi, selectLayout]);

  const gridOptions: GridOptions = {
    suppressDragLeaveHidesColumns: true,
    suppressMakeColumnVisibleAfterUnGroup: true,
    sideBar: false,
    columnDefs: columns,
    defaultColDef: GridUtils.defaultColDef,
    animateRows: true,
    rowSelection: "multiple",
    rowMultiSelectWithClick: true,
    // 行全体に色をつける　現在はコメントアウト
    // rowClassRules: GridUtils.procRowClassRules,
    multiSortKey: "ctrl",
    components: { dateTimeEditor: DateTimeEditor }, // カスタムエディタを登録
    localeText: GridUtils.gridLocaleText,
    onRowClicked: (event) => {
      if (gridApi) {
        dispatch(setCheckedRowUpdateAt(new Date().toISOString())); // チェックした行のデータはapi.getSelectedRows()経由で取得する
      }
      /*
      // event.node はクリックされた行に関する情報を持つオブジェクト
      // isSelected() メソッドでその行が選択されているかどうかを確認できる
      if (event.node.isSelected()) {
        // setSelected(false) でその行の選択を解除
        event.node.setSelected(false);
      } else {
        // setSelected(true) でその行を選択
        event.node.setSelected(true);
      }*/
    },
    rowDragManaged: true,
    onRowDragEnd: (event) => handleRowDragEnd(event, gridOptions.api),
    // getRowId: (data: any) => data.id,
    enableFillHandle: true,
    fillOperation: calculateFillOperation,
    singleClickEdit: false,
    onCellValueChanged: (event) => {
      if (event.column.getColId() === "edited_schedule_time_start") {
        const node = event.node;
        const data = node.data;
        const newScheduledDateEnd = GridUtils.calculateScheduledDateEnd(
          data.edited_schedule_time_start,
          data.simulated_proc_time
        );
        node.setDataValue("edited_schedule_time_end", newScheduledDateEnd);
      }
      const updatedFilteredData: any[] = [...filteredRowData]; // 現在のfilteredRowDataをコピー
      const index = updatedFilteredData.findIndex(
        (row) => row.id === event.data.id
      ); // 編集された行を探す
      if (index !== -1) {
        updatedFilteredData[index] = {
          ...updatedFilteredData[index],
          ...event.data,
        }; // 編集された行を更新
      }
      setFilteredRowData(updatedFilteredData); // 状態変数を更新
    },
    onColumnValueChanged(event) {
      //console.log(event)
    },
    onSelectionChanged(event) {
      //console.log(event)
      if (
        (event.source === "checkboxSelected" ||
          event.source === "uiSelectAll") &&
        gridApi
      ) {
        dispatch(setCheckedRowUpdateAt(new Date().toISOString())); // チェックした行のデータはapi.getSelectedRows()経由で取得する
        /* チェックしたものを先頭にソートする場合はコメント解除
        columnApi.applyColumnState({
          state: [
          ],
          defaultState: { sort: null },
        });
        columnApi.applyColumnState({
          state: [
            { colId: 'chkrow', sort: 'desc', sortIndex: 0 },
          ],
          defaultState: { sort: null },
        });
        */
      }
    },
  };

  // ボタンがクリックされたときに状態を変更する関数
  const handleCheckboxChange = () => {
    // const isChecked = e.target.checked;
    dispatch(toggleTemperingScheduleMode()); // 変更: 状態をトグルする

    if (gridApi) {
      // gridApiがnullでない場合
      columnApi.setColumnVisible("proc_order", !isSchedulingMode);
      columnApi.setColumnVisible(
        "edited_schedule_time_start",
        !isSchedulingMode
      );
      columnApi.setColumnVisible("edited_schedule_time_end", !isSchedulingMode);
      columnApi.setColumnVisible(
        "is_fin_proc_schedule_complete",
        !isSchedulingMode
      );
      // スケジュール中は見えなくする
      columnApi.setColumnVisible("id", isSchedulingMode);
      columnApi.setColumnVisible("mach_code", isSchedulingMode);
    }
  };

  return (
    <>
      <div className="grid-container">
        <RunningTotal rowData={filteredRowData} />
        {/* 実績一覧レイアウト選択 */}
        <div className="proc-list-layout-container">
          <ProcListLayoutSelecter />
          {/* <ToggleSwitch
            isChecked={isSchedulingMode}
            onToggle={handleCheckboxChange}
            label={"詳細スケジュール"}
          ></ToggleSwitch> */}
          {/* <ToggleSwitch
            isChecked={isShowGraph}
            onToggle={handleChangeGraphTggle}
            label={"グラフ"}
          ></ToggleSwitch> */}
        </div>
        <div
          className="ag-checkbox ag-theme-alpine"
          style={{ width: "100%", height: gridHeight }}
        >
          <AgGridReact
            {...gridOptions}
            ref={gridRef}
            rowData={rowData}
            onGridReady={onGridReadyHandler}
            enableRangeSelection={true}
            context={{ openMergedChildModal }}
            onFilterChanged={(params) => {
              const filteredData: any[] = [];
              params.api.forEachNodeAfterFilter((node: IRowNode) => {
                filteredData.push(node.data);
              });
              setFilteredRowData(filteredData); // 状態変数を更新
            }}
          />

          {/* <p>デフォルトの並び順: 緊急(↓), 基準日(↑), 板厚(↑), 注文NO(↑)</p><br /> */}
        </div>
      </div>

      {isShowGraph ? (
        <div className="chart-container">
          <Chart
            rowData={filteredRowData}
            axisField="proc_date"
            chartTitle="仕事量(基準日ベース)"
          />
          <Chart
            rowData={filteredRowData}
            axisField="schedule_start_time"
            chartTitle="仕事量(計画日ベース)"
          />
          <CostChart
            rowData={filteredRowData}
            axisField="schedule_start_time"
            chartTitle="金額(計画日ベース)"
          />
        </div>
      ) : (
        ""
      )}
      {selectedMergedChildren.length > 0 && (
        <MergedChildModal
          isOpen={isMergedChildModalOpen}
          onClose={closeMergedChildModal}
          mergedChildren={selectedMergedChildren}
        />
      )}
    </>
  );
};

export default Grid;
